import { useState, useEffect } from 'react';
import { getArgumentTemplates } from '../lib/api';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { 
  FileText, 
  Loader2,
  Lightbulb,
  Copy,
  Check,
  ChevronRight
} from 'lucide-react';

export default function ArgumentTemplates({ isOpen, onClose, onSelectTemplate }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadTemplates();
    }
  }, [isOpen]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const res = await getArgumentTemplates();
      setTemplates(res.data.templates || []);
      if (res.data.templates?.length > 0) {
        setSelectedTemplate(res.data.templates[0]);
      }
    } catch (err) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyTemplate = () => {
    if (selectedTemplate?.template) {
      navigator.clipboard.writeText(selectedTemplate.template);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Template copied to clipboard');
    }
  };

  const handleUseTemplate = () => {
    if (selectedTemplate?.template && onSelectTemplate) {
      onSelectTemplate(selectedTemplate.template);
      onClose();
      toast.success(`${selectedTemplate.name} template applied`);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-bronze" />
            Case Argument Templates
          </DialogTitle>
          <DialogDescription>
            Choose a template to help structure your case arguments for AI analysis
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-bronze" />
          </div>
        ) : (
          <div className="flex-1 overflow-hidden flex gap-4">
            {/* Template List */}
            <div className="w-64 flex-shrink-0 border-r pr-4">
              <ScrollArea className="h-[50vh]">
                <div className="space-y-2">
                  {templates.map((template) => (
                    <Card 
                      key={template.id}
                      className={`cursor-pointer transition-all ${
                        selectedTemplate?.id === template.id 
                          ? 'border-bronze bg-bronze/5' 
                          : 'hover:border-bronze/50'
                      }`}
                      onClick={() => setSelectedTemplate(template)}
                      data-testid={`template-${template.id}`}
                    >
                      <CardContent className="p-3">
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{template.icon}</span>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{template.name}</p>
                          </div>
                          {selectedTemplate?.id === template.id && (
                            <ChevronRight className="w-4 h-4 text-bronze flex-shrink-0" />
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </ScrollArea>
            </div>

            {/* Template Details */}
            {selectedTemplate && (
              <div className="flex-1 overflow-hidden flex flex-col">
                <div className="mb-4">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-3xl">{selectedTemplate.icon}</span>
                    <div>
                      <h3 className="font-semibold text-lg">{selectedTemplate.name}</h3>
                      <p className="text-sm text-muted-foreground">{selectedTemplate.description}</p>
                    </div>
                  </div>
                </div>

                <Tabs defaultValue="template" className="flex-1 overflow-hidden flex flex-col">
                  <TabsList className="w-full justify-start">
                    <TabsTrigger value="template">Template</TabsTrigger>
                    <TabsTrigger value="prompts">Guiding Questions</TabsTrigger>
                  </TabsList>

                  <TabsContent value="template" className="flex-1 overflow-hidden mt-4">
                    <Card className="h-full flex flex-col">
                      <CardHeader className="pb-2 flex-shrink-0">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-sm">Argument Template</CardTitle>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={handleCopyTemplate}
                            data-testid="copy-template-btn"
                          >
                            {copied ? (
                              <><Check className="w-4 h-4 mr-1" />Copied</>
                            ) : (
                              <><Copy className="w-4 h-4 mr-1" />Copy</>
                            )}
                          </Button>
                        </div>
                        <CardDescription className="text-xs">
                          Use this template as a starting point for your case arguments
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="flex-1 overflow-hidden">
                        <ScrollArea className="h-[30vh]">
                          <pre className="text-sm whitespace-pre-wrap font-mono bg-muted/50 p-4 rounded-lg">
                            {selectedTemplate.template}
                          </pre>
                        </ScrollArea>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  <TabsContent value="prompts" className="flex-1 overflow-hidden mt-4">
                    <Card className="h-full">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Guiding Questions</CardTitle>
                        <CardDescription className="text-xs">
                          Answer these questions to build strong case arguments
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <ScrollArea className="h-[30vh]">
                          <div className="space-y-3">
                            {selectedTemplate.prompts?.map((prompt, i) => (
                              <div key={i} className="flex gap-3 p-3 bg-muted/30 rounded-lg">
                                <Badge variant="outline" className="flex-shrink-0 h-6 w-6 rounded-full p-0 flex items-center justify-center">
                                  {i + 1}
                                </Badge>
                                <p className="text-sm">{prompt}</p>
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      </CardContent>
                    </Card>
                  </TabsContent>
                </Tabs>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            onClick={handleUseTemplate}
            className="bg-bronze hover:bg-bronze-light"
            disabled={!selectedTemplate}
            data-testid="use-template-btn"
          >
            <FileText className="w-4 h-4 mr-2" />
            Use This Template
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
